import { makeAutoObservable } from 'mobx';
import {
  ApportionmentAssignment,
  NominalCodeApportionment,
  TrackingCategoryApportionment,
} from '../../../api/ApportionmentApi';
import { ApportionmentType } from '../../../config/Constants';
import { AssignmentSetting } from './AssignmentSetting';

export class NominalCodeAssignment {
  externalId: string = '';

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: NominalCodeApportionment): NominalCodeAssignment {
    const nc = new NominalCodeAssignment();

    nc.externalId = apiData.ncExternalId;

    return nc;
  }

  toApi = (): NominalCodeApportionment => ({
    ncExternalId: this.externalId,
  });
}

export class TrackingCategoryAssignment {
  nameId: string = '';
  typeId: string = '';

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(
    apiData: TrackingCategoryApportionment
  ): TrackingCategoryAssignment {
    const tc = new TrackingCategoryAssignment();

    tc.typeId = apiData.typeId;
    tc.nameId = apiData.nameId;

    return tc;
  }

  toApi = (): TrackingCategoryApportionment => ({
    typeId: this.typeId,
    nameId: this.nameId,
  });
}

export class Assignment {
  id: number = 0;
  entityId: number = 0;
  type: ApportionmentType = ApportionmentType.ASSIGNMENT;
  nominalCode: NominalCodeAssignment = null;
  trackingCategories: Map<string, TrackingCategoryAssignment> = new Map();

  codeError: boolean = false;
  categoryError: boolean = false;

  constructor() {
    makeAutoObservable(this);
    this.nominalCode = new NominalCodeAssignment();
  }

  static fromApi(apiData: ApportionmentAssignment) {
    const assignment = new Assignment();

    assignment.id = apiData.id;
    assignment.entityId = apiData.organisationId;
    assignment.nominalCode = NominalCodeAssignment.fromApi(apiData.nominalCode);
    assignment.trackingCategories = new Map(
      apiData.trackingCategories.map(tc => [
        tc.typeId,
        TrackingCategoryAssignment.fromApi(tc),
      ])
    );

    return assignment;
  }

  static fromAssignmentSettings(
    entityId: number,
    settings: AssignmentSetting
  ): Assignment {
    const assignment = new Assignment();

    assignment.entityId = entityId;
    assignment.nominalCode.externalId = settings?.ncExternalId || '';

    [...(settings?.trackingCategories.entries() || [])].forEach(entry =>
      assignment.setTrackingCategory(entry[0], entry[1])
    );

    return assignment;
  }

  static fromAssignment(other: Assignment): Assignment {
    const assignment = new Assignment();
    assignment.entityId = other.entityId;
    assignment.nominalCode.externalId = other.nominalCode?.externalId;
    other.trackingCategories.forEach(tc =>
      assignment.setTrackingCategory(tc.typeId, tc.nameId)
    );
    return assignment;
  }

  toApi = (): ApportionmentAssignment => {
    return {
      id: this.id,
      type: this.type,
      organisationId: this.entityId,
      nominalCode: this.nominalCode.toApi(),
      trackingCategories: [...this.trackingCategories.values()].map(tc =>
        tc.toApi()
      ),
    };
  };

  setTrackingCategory(typeId: string, nameId: string) {
    if (!this.trackingCategories.has(typeId)) {
      this.trackingCategories.set(typeId, new TrackingCategoryAssignment());
    }
    this.trackingCategories.get(typeId).nameId = nameId;
    this.trackingCategories.get(typeId).typeId = typeId;
  }
}
